// src/contexts/AssessmentContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const AssessmentContext = createContext();

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
};

export const AssessmentProvider = ({ children }) => {
  const [assessmentData, setAssessmentData] = useState({
    organizationDetails: null,
    questions: [],
    answers: {},
    sessionToken: null,
    assessmentId: null
  });

  // Load from sessionStorage on mount
  useEffect(() => {
    const savedData = sessionStorage.getItem('assessmentData');
    const sessionToken = sessionStorage.getItem('sessionToken');
    const assessmentId = sessionStorage.getItem('assessmentId');
    
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      console.log('Loaded assessment data from storage:', parsedData);
      
      // If sessionToken is missing from saved data but exists in sessionStorage, add it
      if (!parsedData.sessionToken && sessionToken) {
        parsedData.sessionToken = sessionToken;
        console.log('Added missing sessionToken from sessionStorage:', sessionToken);
      }
      if (!parsedData.assessmentId && assessmentId) {
        parsedData.assessmentId = parseInt(assessmentId);
        console.log('Added missing assessmentId from sessionStorage:', assessmentId);
      }
      
      setAssessmentData(parsedData);
    } else if (sessionToken) {
      // If we have session token but no saved data, initialize with token
      console.log('No saved data but found sessionToken, initializing:', sessionToken);
      setAssessmentData(prev => ({
        ...prev,
        sessionToken,
        assessmentId: assessmentId ? parseInt(assessmentId) : null
      }));
    }
  }, []);

  // Save to sessionStorage whenever data changes
  useEffect(() => {
    if (assessmentData.sessionToken || Object.keys(assessmentData.answers).length > 0 || assessmentData.questions.length > 0) {
      console.log('Saving assessment data to storage:', assessmentData);
      sessionStorage.setItem('assessmentData', JSON.stringify(assessmentData));
      
      // Also save individual items to sessionStorage for backup
      if (assessmentData.sessionToken) {
        sessionStorage.setItem('sessionToken', assessmentData.sessionToken);
      }
      if (assessmentData.assessmentId) {
        sessionStorage.setItem('assessmentId', assessmentData.assessmentId.toString());
      }
    }
  }, [assessmentData]);

  const updateOrganizationDetails = (details) => {
    console.log('Updating organization details:', details);
    setAssessmentData(prev => ({
      ...prev,
      organizationDetails: details
    }));
  };

  const updateQuestions = (questions) => {
    console.log('Updating questions:', questions.length);
    // Merge answers from sessionStorage if present and not empty
    let mergedAnswers = undefined;
    try {
      const sessionDataRaw = sessionStorage.getItem('assessmentData');
      if (sessionDataRaw) {
        const sessionData = JSON.parse(sessionDataRaw);
        if (sessionData.answers && Object.keys(sessionData.answers).length > 0) {
          mergedAnswers = sessionData.answers;
        }
      }
    } catch {}
    setAssessmentData(prev => ({
      ...prev,
      questions: questions,
      ...(mergedAnswers ? { answers: mergedAnswers } : {})
    }));
  };

  const updateAnswer = (questionId, answer) => {
    console.log('Updating answer for question', questionId, ':', answer);
    setAssessmentData(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: {
          ...answer,
          question_id: questionId
        }
      }
    }));
  };

  const updateSessionInfo = (sessionToken, assessmentId) => {
    console.log('Updating session info:', { sessionToken, assessmentId });
    setAssessmentData(prev => ({
      ...prev,
      sessionToken,
      assessmentId
    }));
    
    // Also update sessionStorage immediately
    if (sessionToken) {
      sessionStorage.setItem('sessionToken', sessionToken);
    }
    if (assessmentId) {
      sessionStorage.setItem('assessmentId', assessmentId.toString());
    }
  };

  const getAnsweredQuestionsCount = () => {
    return Object.keys(assessmentData.answers).length;
  };

  const getTotalQuestionsCount = () => {
    return assessmentData.questions.length;
  };

  const getAnswerForQuestion = (questionId) => {
    return assessmentData.answers[questionId] || null;
  };

  const clearAssessmentData = () => {
    setAssessmentData({
      organizationDetails: null,
      questions: [],
      answers: {},
      sessionToken: null,
      assessmentId: null
    });
    sessionStorage.removeItem('assessmentData');
    sessionStorage.removeItem('sessionToken');
    sessionStorage.removeItem('assessmentId');
    sessionStorage.removeItem('accessLink');
  };

  // Function to check if we have valid assessment data
  const hasValidAssessment = () => {
    return (
      assessmentData.sessionToken || 
      sessionStorage.getItem('sessionToken') ||
      (assessmentData.questions && assessmentData.questions.length > 0) ||
      (assessmentData.answers && Object.keys(assessmentData.answers).length > 0)
    );
  };

  // Function to get current session token from either context or storage
  const getCurrentSessionToken = () => {
    return assessmentData.sessionToken || sessionStorage.getItem('sessionToken');
  };

  return (
    <AssessmentContext.Provider
      value={{
        assessmentData,
        updateOrganizationDetails,
        updateQuestions,
        updateAnswer,
        updateSessionInfo,
        getAnsweredQuestionsCount,
        getTotalQuestionsCount,
        getAnswerForQuestion,
        clearAssessmentData,
        hasValidAssessment,
        getCurrentSessionToken
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};